import { pipe } from 'fp-ts/function';
import { option } from 'fp-ts';
import { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { Banner, Stack } from '@optimus-web/core';
import { ErrorHandler, OverlayedLoader, SmallContentContainer, TextWithLinks } from '@mews-commander/ui';
import { LocalizedText, lt, showSuccessAlert, useLocalizeText } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { WithBillId } from '@mews-commander/types';
import { AddressUpdateParameters, BillType } from '@mews-commander/data-access';
import { useResolveRoute } from '@mews/routing';
import { UnicodeEntity } from '@mews/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { canEnterpriseUseAllowCustomerBillAddressObjection } from '@mews-commander/legacy-entities/src/selectors/enterprises';
import { useCommanderSelector } from '@mews-commander/utils';
import { useCloseButton } from '../../../core/windowManager/hooks';
import { navigateInModal } from '../../app/actions/common';
import { BaseLayout } from '../../../components/layout';
import { useChangeBillingDetails, useFormPrefill } from './hooks';
import { ChangeBillingDetailsForm } from './ChangeBillingDetailsForm';
import { AddressFormValues } from './types';
import { useGetBillName } from './hooks/useGetBillName';

const createAddressUpdateObject = ({
    countryCode,
    countrySubdivisionCode,
    ...other
}: AddressFormValues): AddressUpdateParameters => ({
    ...other,
    countryCode: countryCode === '' ? null : countryCode,
    subdivisionCode: countrySubdivisionCode === '' ? null : countrySubdivisionCode,
});

const ChangeBillingDetails = ({ billId }: WithBillId) => {
    const closeButton = useCloseButton();
    const dispatch = useDispatch();
    const localizeText = useLocalizeText();
    const resolveRoute = useResolveRoute();

    const enterpriseId = useCurrentEnterpriseId();
    const allowCustomerBillAddressObjection = useCommanderSelector((state) =>
        canEnterpriseUseAllowCustomerBillAddressObjection(state, { enterpriseId })
    );

    const billNameResult = useGetBillName(billId);
    const result = useFormPrefill(billId, allowCustomerBillAddressObjection);
    const mutation = useChangeBillingDetails();

    const billActionLogUrl = resolveRoute(RouteName.BillActionLog, { id: billId });

    return (
        <BaseLayout
            title={<LocalizedText textKey={TextKey.ChangeBillingDetailsModalHeaderTitle} />}
            primaryAction={closeButton}
        >
            <SmallContentContainer>
                {result.isFetching || billNameResult.isFetching || mutation.isPending ? (
                    <OverlayedLoader show fixed />
                ) : null}
                {result.isError || billNameResult.isError ? <ErrorHandler /> : null}
                {pipe(
                    result.data,
                    option.map(({ billType, ...defaultValues }) => (
                        <Stack spacing="200" vertical>
                            <Banner
                                variant="info"
                                primaryText={
                                    <LocalizedText
                                        textKey={
                                            billType === BillType.Receipt
                                                ? TextKey.ChangeBillingDetailsBannerTitle
                                                : TextKey.ChangeBillingDetailsInvoiceBannerTitle
                                        }
                                    />
                                }
                                secondaryText={
                                    <>
                                        <LocalizedText
                                            textKey={TextKey.ChangeBillingDetailsMainBannerDescription}
                                            substitutes={{
                                                documentName:
                                                    billNameResult.data ?? localizeText({ textKey: TextKey.Bill }),
                                            }}
                                        />
                                        {UnicodeEntity.Space}
                                        <TextWithLinks
                                            links={[
                                                {
                                                    id: 'actionLog',
                                                    url: billActionLogUrl,
                                                    text: localizeText({
                                                        textKey: TextKey.ChangeBillingDetailsActionLogLink,
                                                    }),
                                                },
                                            ]}
                                            text={localizeText({
                                                textKey: TextKey.ChangeBillingDetailsBannerDescriptionActionLog,
                                            })}
                                        />
                                    </>
                                }
                            />
                            <ChangeBillingDetailsForm
                                billData={result.billData}
                                allowCustomerBillAddressObjection={allowCustomerBillAddressObjection}
                                defaultValues={defaultValues}
                                onSubmit={({ owner, associatedAccount, creditNoteCounterId }) => {
                                    mutation.mutate(
                                        {
                                            billId,
                                            ownerAddress: createAddressUpdateObject(owner.address),
                                            ownerAddressObjection: owner.ownerAddressObjection,
                                            associatedAccountId: associatedAccount.id,
                                            associatedAccountAddress:
                                                associatedAccount.id &&
                                                !associatedAccount.associatedAccountAddressObjection
                                                    ? createAddressUpdateObject(associatedAccount.address)
                                                    : null,
                                            associatedAccountAddressObjection:
                                                associatedAccount.associatedAccountAddressObjection,
                                            creditNoteCounterId,
                                        },
                                        {
                                            onSuccess: ({ id, name, creditNoteTitle }) => {
                                                const billDetailUrl = resolveRoute(RouteName.BillDetail, { id });
                                                dispatch(navigateInModal(billDetailUrl));

                                                showSuccessAlert({
                                                    title: lt('BillingAddressUpdatedSuccessMessageTitle'),
                                                    description: lt('BillingAddressChangeSuccessMessageDescription', {
                                                        newBill: name ?? localizeText({ textKey: TextKey.Bill }),
                                                        creditNote:
                                                            creditNoteTitle ??
                                                            localizeText({ textKey: TextKey.CreditNote }),
                                                    }),
                                                });
                                            },
                                        }
                                    );
                                }}
                                billId={billId}
                            />
                        </Stack>
                    )),
                    option.getOrElse<ReactNode>(() => null)
                )}
            </SmallContentContainer>
        </BaseLayout>
    );
};

export default ChangeBillingDetails;
